import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, MessageCircle, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVIZI = [
  "Allevamento",
  "Toelettatura",
  "Pensione per cani",
  "Cibo per cani",
  "Negozio animali e pesca",
  "Altro",
];

const FAQS = [
  {
    q: "Come posso contattarvi?",
    a: `Puoi chiamarci o scriverci su WhatsApp al ${SITE.phoneDisplay} (Nicola), inviare un'email a ${SITE.email} oppure compilare il form qui sopra.`,
  },
  {
    q: "È necessario prenotare la toelettatura?",
    a: "Sì, è preferibile prenotare in anticipo per garantire la migliore esperienza al tuo cane e ridurre i tempi di attesa.",
  },
  {
    q: "La pensione è disponibile tutto l'anno?",
    a: "Sì, il nostro servizio di pensione è attivo tutto l'anno, anche nei periodi festivi. Ti consigliamo di richiedere disponibilità con anticipo, soprattutto nei periodi di alta stagione.",
  },
  {
    q: "Offrite consulenza per il cibo più adatto?",
    a: "Certamente. In base all'età, alla taglia e alle esigenze del tuo cane, ti aiutiamo a individuare l'alimento più adatto.",
  },
  {
    q: "Avete anche prodotti per animali?",
    a: "Sì, nel nostro negozio trovi accessori, guinzagli, collari, giochi, prodotti per la cura, alimenti e molto altro.",
  },
  {
    q: "Vendete articoli per la pesca?",
    a: "Sì, mettiamo a disposizione anche una selezione di prodotti e accessori per la pesca.",
  },
];

export default function Contatti() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.servizio || !form.messaggio) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm({ nome: "", email: "", telefono: "", servizio: "", messaggio: "" });
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contatti">
      <section className="pt-12 pb-10 sm:pt-20" data-testid="contatti-hero">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12 text-center animate-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#7A1F1F] mb-5">
            Contatti
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[#1A1710]">
            Scrivici o chiamaci: <em className="italic text-[#7A1F1F]">siamo qui per te</em>.
          </h1>
          <p className="mt-5 text-lg text-[#6B5F4A]">
            Hai bisogno di informazioni? Saremo felici di aiutarti a scegliere
            la soluzione più adatta per il tuo animale.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* INFO */}
          <aside className="lg:col-span-5 space-y-4">
            <a
              href={SITE.mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-info-address"
              className="block bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#EDD895] grid place-items-center text-[#7A1F1F] flex-none">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">Indirizzo</div>
                  <div className="font-semibold text-[#1A1710] mt-1">{SITE.address}</div>
                  <div className="text-sm text-[#6B5F4A] mt-1">Apri in Google Maps →</div>
                </div>
              </div>
            </a>

            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="contact-info-phone"
              className="block bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#EDD895] grid place-items-center text-[#8B4A2B] flex-none">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">Telefono</div>
                  <div className="font-semibold text-[#1A1710] mt-1">{SITE.phoneDisplay}</div>
                  <div className="text-sm text-[#6B5F4A] mt-1">{SITE.owner} — disponibile durante il giorno</div>
                </div>
              </div>
            </a>

            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-info-whatsapp"
              className="block bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#25D366]/15 grid place-items-center text-[#25D366] flex-none">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">WhatsApp</div>
                  <div className="font-semibold text-[#1A1710] mt-1">Scrivici subito</div>
                  <div className="text-sm text-[#6B5F4A] mt-1">Risposta rapida e diretta</div>
                </div>
              </div>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-info-email"
              className="block bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#EDD895] grid place-items-center text-[#7A1F1F] flex-none">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">Email</div>
                  <div className="font-semibold text-[#1A1710] mt-1">{SITE.email}</div>
                </div>
              </div>
            </a>

            <div
              data-testid="contact-info-hours"
              className="bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#EDD895] grid place-items-center text-[#8B4A2B] flex-none">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#7A1F1F] font-semibold">Orari</div>
                  <div className="font-semibold text-[#1A1710] mt-1">Su appuntamento</div>
                  <div className="text-sm text-[#6B5F4A] mt-1">Aperti tutto l'anno — pensione 7 su 7</div>
                </div>
              </div>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-7 sm:p-10"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1710] leading-tight">
                Compila il modulo
              </h2>
              <p className="mt-2 text-[#6B5F4A]">
                Riceverai una risposta personale da Nicola o Elisa.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="nome" className="text-sm font-medium text-[#1A1710]">
                    Nome e cognome <span className="text-[#8B4A2B]">*</span>
                  </Label>
                  <Input
                    id="nome"
                    data-testid="contact-input-nome"
                    value={form.nome}
                    onChange={handleChange("nome")}
                    placeholder="Mario Rossi"
                    className="mt-2 rounded-xl border-[#D9C48A] bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#1A1710]">
                    Email <span className="text-[#8B4A2B]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="nome@email.it"
                    className="mt-2 rounded-xl border-[#D9C48A] bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="telefono" className="text-sm font-medium text-[#1A1710]">
                    Telefono
                  </Label>
                  <Input
                    id="telefono"
                    data-testid="contact-input-telefono"
                    value={form.telefono}
                    onChange={handleChange("telefono")}
                    placeholder="+39 ..."
                    className="mt-2 rounded-xl border-[#D9C48A] bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="servizio" className="text-sm font-medium text-[#1A1710]">
                    Servizio di interesse <span className="text-[#8B4A2B]">*</span>
                  </Label>
                  <Select
                    value={form.servizio}
                    onValueChange={(v) => setForm((f) => ({ ...f, servizio: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-select-servizio"
                      className="mt-2 rounded-xl border-[#D9C48A] bg-white"
                    >
                      <SelectValue placeholder="Seleziona un servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVIZI.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`servizio-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="messaggio" className="text-sm font-medium text-[#1A1710]">
                    Messaggio <span className="text-[#8B4A2B]">*</span>
                  </Label>
                  <Textarea
                    id="messaggio"
                    data-testid="contact-input-messaggio"
                    value={form.messaggio}
                    onChange={handleChange("messaggio")}
                    rows={6}
                    placeholder="Raccontaci come possiamo aiutarti..."
                    className="mt-2 rounded-xl border-[#D9C48A] bg-white resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-8 inline-flex items-center gap-2 bg-[#7A1F1F] hover:bg-[#5C1717] disabled:opacity-60 text-white rounded-full px-8 py-4 text-sm font-semibold transition-colors"
              >
                {loading ? "Invio in corso..." : "Invia richiesta"}
                <Send className="w-4 h-4" />
              </button>
              <p className="mt-4 text-xs text-[#6B5F4A]">
                Inviando il modulo accetti di essere contattato per rispondere alla tua richiesta.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* MAPPA */}
      <section className="py-12 sm:py-16" data-testid="mappa">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <SectionHeading
            overline="Dove siamo"
            title="Vieni a trovarci a Guidizzolo (MN)."
          />
          <div className="mt-10 rounded-3xl overflow-hidden border border-[#D9C48A] aspect-[16/9]">
            <iframe
              title="Mappa Colli Storici"
              src={SITE.mapsEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full border-0"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24 bg-[#EDD895]/60" data-testid="faq">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-12">
          <SectionHeading
            overline="Domande frequenti"
            title="Le risposte alle domande più comuni."
          />
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-[#D9C48A]"
              >
                <AccordionTrigger className="text-left font-serif text-xl sm:text-2xl text-[#1A1710] hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#6B5F4A] leading-relaxed text-base pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
